import { BudgetBys, BudgetPhases } from 'business/constants';
import _ from 'lodash';
import {
    BudgetProposalT,
    BudgetVentilatedDataT,
    BudgetVentilatedDataWithCadrageT,
    CadrageT,
    ResourceWithNameAndCodeT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import {
    REALIZED_FIELD,
    REQUESTED_FIELD,
    VOTED_TOTAL_FIELD,
    PERCENT_REALIZED_FIELD,
    DEPASSEMENT_METRIC,
    EVOLUTION_N_N_1,
    VIEW_DETAILS,
    getPhaseInscriptionTableName,
} from 'routes/BudgetModule/config/constants';
import { manageUnknownCellValue } from 'helpers/formatHelper';
import { getColnameValueByYear } from './format';
import { getResourceSoftwareIdField } from './budgetProposalHelpers';
import { ExpenseIncomeImputationDataT } from '../components/BudgetProposal/Resource/ExpenseIncome/hooks/expenseIncomeImputations';
import {
    getFieldValueFromPhase,
    InscriptionT,
    PhaseInscriptionT,
} from '../shared/entities/inscription';

const getRate = (
    a: ResponseDataValueT,
    b: ResponseDataValueT,
): ResponseDataValueT => {
    if (typeof a === 'number' && typeof b === 'number' && b > 0 && a !== 0)
        return (a - b) / b;
    if (a === 0 || (typeof b === 'number' && b <= 0)) return NaN;
    return NaN;
};

/**
 * Returns the cadrage value for a given resource software ID.
 */
export const getCadrageValueForResource = (
    resourceSoftwareId: string,
    cadrageData: CadrageT[],
): number => {
    const matchingCadrageRows = cadrageData.filter(
        (item) => item.resource === resourceSoftwareId,
    );
    return matchingCadrageRows.reduce((sum, item) => sum + item.amount, 0);
};

const getCadrageValue = (
    row: BudgetVentilatedDataT,
    cadrageData: CadrageT[],
    budgetProposalBy: BudgetBys,
): number => {
    const ventilationField = getResourceSoftwareIdField(
        budgetProposalBy,
    ) as keyof ResourceWithNameAndCodeT;
    return getCadrageValueForResource(row[ventilationField], cadrageData);
};

export const addCadrageToVentilatedData = (
    pivotedByResource: BudgetVentilatedDataT[],
    cadrageData: CadrageT[],
    budgetProposalBy: BudgetBys,
): BudgetVentilatedDataWithCadrageT[] =>
    pivotedByResource.map((row) => {
        const cadrageValue = getCadrageValue(
            row,
            cadrageData,
            budgetProposalBy,
        );
        return {
            ...row,
            cadrage: cadrageValue,
            [DEPASSEMENT_METRIC]: getRate(row.expense, cadrageValue),
            [VIEW_DETAILS.VIEW]: VIEW_DETAILS.CONSULT,
        };
    });

const getRowEvolution = (
    row: ExpenseIncomeImputationDataT,
    year: number,
): ResponseDataValueT => {
    const requestedThisYear =
        row[
            getColnameValueByYear(
                REQUESTED_FIELD,
                year,
            ) as `${typeof REQUESTED_FIELD}..${number}`
        ];
    const votedLastYear =
        row[
            getColnameValueByYear(
                VOTED_TOTAL_FIELD,
                year - 1,
            ) as `${typeof VOTED_TOTAL_FIELD}..${number}`
        ];
    return getRate(requestedThisYear, votedLastYear);
};

const addEvolutionToImputationGrid = (
    imputationData: ExpenseIncomeImputationDataT[],
    year: number,
): ExpenseIncomeImputationDataT[] =>
    imputationData.map((row) => ({
        ...row,
        [getColnameValueByYear(EVOLUTION_N_N_1, year)]: getRowEvolution(
            row,
            year,
        ),
    }));

const hidePercentRealized = (
    imputationData: ExpenseIncomeImputationDataT[],
    year: number,
): ExpenseIncomeImputationDataT[] =>
    imputationData.map((row) => {
        const rowT = row;
        const realized = getColnameValueByYear(
            REALIZED_FIELD,
            year,
        ) as `${typeof REALIZED_FIELD}..${number}`;
        const percentRealized = getColnameValueByYear(
            PERCENT_REALIZED_FIELD,
            year,
        ) as `${typeof PERCENT_REALIZED_FIELD}..${number}`;
        if (row[realized] === 0) rowT[percentRealized] = 0;
        return rowT;
    });

export const manageUnknownValue = <DataT extends ResponseDataT>(
    data: DataT[],
): DataT[] => data.map((row) => manageUnknownCellValue(row));

// TODO: test this function
/**
 * Transforms the given imputation data by applying a series of operations:
 * 1. Manages unknown values in the imputation data.
 * 2. Adds evolution data to the imputation grid based on the provided budget proposal's exercise.
 * 3. Hides the percent realized in the imputation grid based on the provided budget proposal's exercise.
 *
 * @param imputationData - An array of imputation data objects to be transformed.
 * @param bp - The budget proposal containing the exercise information.
 * @returns The transformed imputation data array.
 */
export const operationsToImputationGrid = (
    imputationData: ExpenseIncomeImputationDataT[],
    bp: BudgetProposalT,
): ExpenseIncomeImputationDataT[] =>
    hidePercentRealized(
        addEvolutionToImputationGrid(
            manageUnknownValue(imputationData),
            bp.exercice,
        ),
        bp.exercice,
    );

/**
 * Sums the values of a specified metric in the data.
 *
 * @param data - The array of data objects.
 * @param metricName - The name of the metric to sum.
 * @returns The sum of the metric values.
 */
export const getSumOfMetric = (
    data: { [key: string]: unknown }[],
    metricName: string,
): number =>
    data.reduce((prev, current) => {
        const currentValue = current[metricName];
        const isNumber = typeof currentValue === 'number';
        return isNumber ? prev + currentValue : prev;
    }, 0);

/**
 * Sums the values of a specified metric in the data using the getter.
 *
 * @param data - The array of data objects.
 * @param metricName - The name of the metric to sum.
 * @returns The sum of the metric values.
 */
export const getSumOfMetricByGetter = (
    data: (InscriptionT | ResponseDataT)[],
    getter: (item: InscriptionT | ResponseDataT) => number | string,
): number =>
    data.reduce((prev, current) => {
        const currentValue = getter(current);
        const isNumber = typeof currentValue === 'number';
        return isNumber ? prev + currentValue : prev;
    }, 0);

export const sumMultiplePhaseAmounts = (
    data: InscriptionT[],
    phases: BudgetPhases[],
    metric: keyof PhaseInscriptionT,
    dim: string,
    // FIXME: remove filterData and filter data upstream
    filterData: (
        inscriptionOrImputation: FlowAnyObject[],
        phase?: BudgetPhases,
    ) => FlowAnyObject[] = (inscriptions) => inscriptions,
): ResponseDataT[] =>
    _(data)
        .groupBy(dim)
        .map((grouped, dimValue) => {
            const sumPhases = phases.map((phase) => ({
                [`${getPhaseInscriptionTableName(phase)}.${metric}`]: _.sumBy(
                    filterData ? filterData(grouped) : grouped,
                    (obj) => {
                        const amount = getFieldValueFromPhase(
                            phase,
                            metric,
                            obj,
                        );
                        return Number.isNaN(amount) ||
                            typeof amount !== 'number'
                            ? 0
                            : amount;
                    },
                ),
            }));
            return Object.assign({ [dim]: dimValue }, ...sumPhases);
        })
        .value();

// TODO : to test
/**
- group the data on a given dimension (dim)
- for each group, sum on the given metrics
 */
export const sumMultipleMetricByDim = (
    data: FlowAnyObject[],
    metrics: string[],
    dim: string,
    // FIXME: remove filterData and statusesSettings and filter data upstream
    statusesSettings: StatusesSettingsT,
    filterData: (
        arg1: string,
        arg2: FlowAnyObject[],
        arg3: StatusesSettingsT,
    ) => FlowAnyObject[] = (metric, inscriptions) => inscriptions,
): ResponseDataT[] =>
    _(data)
        .groupBy(dim)
        .map((grouped, dimValue) => {
            const sumMetrics = metrics.map((metric) => ({
                [metric]: _.sumBy(
                    filterData
                        ? filterData(metric, grouped, statusesSettings)
                        : grouped,
                    (obj) =>
                        Number.isNaN(obj[metric]) ||
                        typeof obj[metric] !== 'number'
                            ? 0
                            : obj[metric],
                ),
            }));
            return Object.assign({ [dim]: dimValue }, ...sumMetrics);
        })
        .value();

// TODO : to test
/**
- group the data on a given dimension (dim)
- merge the group object
 */
export const groupByDim = (
    data: ResponseDataT[],
    dim: string,
): ResponseDataT[] =>
    _(data)
        .groupBy(dim)
        .map((grouped) =>
            grouped.reduce((res, current) => Object.assign(res, current), {}),
        )
        .value();

// TODO : to test
export const getAggregatedData = (
    data: ResponseDataT[],
    dim: string,
    numerator: string,
    resName: string,
): ResponseDataT[] =>
    _(data)
        .groupBy(dim)
        .map((grouped) => {
            const totalNum = grouped.reduce(
                (accNum, current) =>
                    // @ts-expect-error [TS migration] Was not detected by flow
                    accNum + current[numerator],
                0,
            );
            return {
                [resName]: totalNum,
                [dim]: grouped[0][dim],
            };
        })
        .value();
